import { useEffect, useRef, useState, useCallback } from "react";
import { createPortal } from "react-dom";
import confetti from "canvas-confetti";
import { X, Loader2, RefreshCw, Home } from "lucide-react";
import { api, apiError } from "@/lib/api";
import { toast } from "sonner";
import { CoinIcon } from "@/components/CoinBadge";

const G = "/brand/game";
const SCENE_START = `${G}/scene_start.jpg`;       // clean approved START still (no baked chrome)
const SCENE_GOAL = `${G}/scene_goal.jpg`;         // clean approved GOAL still (also video-fallback)
const SCENE_SAVE = `${G}/scene_save.jpg`;         // clean approved SAVE still (also video-fallback)
const VIDEO_GOAL = `${G}/goal_play.mp4?v=3`;      // ?v busts any cached broken decode
const VIDEO_SAVE = `${G}/save_play.mp4?v=3`;
const WEBM_GOAL = `${G}/goal_play.webm?v=3`;      // VP9 so open-source Chromium/Android decodes it
const WEBM_SAVE = `${G}/save_play.webm?v=3`;
const PLAYBACK_RATE = 2.4; // ~7s source -> ~3s gameplay: kick -> flight -> keeper dive -> result

// phases: ready -> playing (approved gameplay video) -> result (video frozen on its final frame).
// Backend decides the outcome first; the matching video plays and freezes on its SAVED!/GOAL! frame,
// so the visual can never contradict the recorded result and the composition stays consistent.
export default function BallChallenge({ open, onClose, onPointsChange }) {
  const [loading, setLoading] = useState(true);
  const [points, setPoints] = useState(0);
  const [remaining, setRemaining] = useState(0);
  const [limit, setLimit] = useState(1);
  const [phase, setPhase] = useState("ready");     // ready | playing | result
  const [result, setResult] = useState(null);      // {outcome, coins}
  const [animated, setAnimated] = useState(false);  // did the video play this round?
  const played = remaining <= 0;
  const goalOutcome = result?.outcome === "GOAL";
  const inflight = useRef(false);
  const goalVid = useRef(null);
  const saveVid = useRef(null);
  const safety = useRef(null);

  useEffect(() => {
    if (!open) return;
    window.history.pushState({ zpGame: true }, "");
    const onPop = () => onClose();
    window.addEventListener("popstate", onPop);
    return () => { window.removeEventListener("popstate", onPop); clearTimeout(safety.current); };
  }, [open]);
  const close = () => { if (window.history.state?.zpGame) window.history.back(); else onClose(); };

  const reset = useCallback(() => {
    clearTimeout(safety.current);
    setResult(null); setPhase("ready"); setAnimated(false); inflight.current = false;
    [goalVid, saveVid].forEach((r) => { if (r.current) { try { r.current.pause(); r.current.currentTime = 0; } catch (_) {} } });
  }, []);

  const load = useCallback(() => {
    setLoading(true); reset();
    api.get("/games/ball-challenge").then(({ data }) => {
      setPoints(data.points || 0);
      setLimit(data.limit ?? 1); setRemaining(data.remaining ?? 0);
      if ((data.remaining ?? 0) <= 0 && data.result) { setResult(data.result); setPhase("result"); }
    }).catch((e) => toast.error(apiError(e))).finally(() => setLoading(false));
  }, [reset]);

  useEffect(() => { if (open) load(); }, [open, load]);

  const toResult = useCallback((data) => {
    clearTimeout(safety.current);
    setPhase((p) => (p === "result" ? p : "result"));
    if (data?.outcome === "GOAL") {
      confetti({ particleCount: 180, spread: 100, startVelocity: 42, origin: { y: 0.4 }, colors: ["#14B8A6", "#F5B301", "#fff", "#22c55e", "#3b82f6"] });
      try { navigator.vibrate && navigator.vibrate([30, 40, 60]); } catch (_) {}
    } else { try { navigator.vibrate && navigator.vibrate(60); } catch (_) {} }
  }, []);

  const shoot = async () => {
    if (played || loading || inflight.current || phase !== "ready") return;
    inflight.current = true;
    // Prime both videos WITHIN the tap gesture (muted) so the later programmatic play() — called
    // after an awaited network request, i.e. outside the gesture — is allowed to autoplay.
    [goalVid, saveVid].forEach((r) => {
      const v = r.current;
      if (!v) return;
      v.muted = true; v.defaultMuted = true;
      try { const p = v.play(); if (p && p.then) p.then(() => { v.pause(); v.currentTime = 0; }).catch(() => {}); } catch (_) {}
    });
    try {
      const { data } = await api.post("/games/ball-challenge/shoot");
      const res = { outcome: data.outcome, coins: data.coins };
      setResult(res);
      setPoints(data.balance); onPointsChange && onPointsChange(data.balance);
      setRemaining(data.remaining ?? 0);
      setAnimated(true); setPhase("playing");
      try { navigator.vibrate && navigator.vibrate(18); } catch (_) {}
      const vid = (data.outcome === "GOAL" ? goalVid : saveVid).current;
      if (vid) {
        try {
          vid.muted = true; vid.currentTime = 0; vid.playbackRate = PLAYBACK_RATE;
          const p = vid.play(); if (p && p.catch) p.catch(() => toResult(res));
        } catch (_) { toResult(res); }
        safety.current = setTimeout(() => toResult(res), 5200); // net if 'ended' never fires
      } else {
        toResult(res);
      }
      inflight.current = false;
    } catch (e) {
      toast.error(apiError(e)); reset();
    }
  };

  if (!open) return null;

  const playing = phase === "playing";
  const isResult = phase === "result";
  const showGoalVid = animated && (playing || isResult) && goalOutcome;
  const showSaveVid = animated && (playing || isResult) && !goalOutcome;
  const showPoster = phase === "ready";
  const showResultStill = isResult && !animated; // already-played state on fresh open
  const shotsLabel = remaining > 0
    ? `${remaining} of ${limit} shot${limit > 1 ? "s" : ""} left today`
    : "That's all your shots for today • come back tomorrow";

  return createPortal((
    <div className="fixed inset-0 z-[100] flex h-[100dvh] flex-col overflow-hidden bg-[#0a1b33] select-none" data-testid="ball-modal">
      <img src={SCENE_START} alt="" className="pointer-events-none absolute inset-0 h-full w-full scale-110 object-cover opacity-40 blur-2xl" />

      <header className="relative z-20 flex items-center justify-between px-4 py-2.5">
        <button onClick={close} data-testid="ball-back-btn" className="grid h-10 w-10 place-items-center rounded-full bg-black/35 text-white backdrop-blur transition hover:bg-black/55 active:scale-90"><X size={20} /></button>
        <div className="flex items-center gap-1.5 rounded-full bg-black/35 px-3 py-1.5 text-white backdrop-blur"><CoinIcon size={18} /><span data-testid="ball-points" className="font-display text-base font-bold">{points.toLocaleString("en-IN")}</span></div>
      </header>

      {/* stage — poster (ready) / gameplay video (playing) / frozen result frame (result) */}
      <div className="relative z-10 min-h-0 flex-1 overflow-hidden" onClick={phase === "ready" ? shoot : undefined} data-testid="ball-pitch">
        {loading ? (
          <Loader2 className="absolute left-1/2 top-1/2 h-8 w-8 -translate-x-1/2 -translate-y-1/2 animate-spin text-white" />
        ) : (
          <>
            <img src={showResultStill ? (goalOutcome ? SCENE_GOAL : SCENE_SAVE) : SCENE_START} alt="Goal & Earn" data-testid="ball-scene"
              className={`absolute inset-0 h-full w-full object-contain transition-opacity duration-150 ${(showPoster || showResultStill) ? "opacity-100" : "opacity-0"}`} style={{ objectPosition: "center" }} />
            <video ref={goalVid} muted playsInline preload="auto" data-testid="ball-video-goal"
              onEnded={() => toResult({ outcome: "GOAL" })}
              onError={() => { setAnimated(false); toResult({ outcome: "GOAL" }); }}
              className={`absolute inset-0 h-full w-full object-contain ${showGoalVid ? "opacity-100" : "pointer-events-none opacity-0"}`}>
              <source src={WEBM_GOAL} type="video/webm" />
              <source src={VIDEO_GOAL} type="video/mp4" />
            </video>
            <video ref={saveVid} muted playsInline preload="auto" data-testid="ball-video-save"
              onEnded={() => toResult({ outcome: "SAVE" })}
              onError={() => { setAnimated(false); toResult({ outcome: "SAVE" }); }}
              className={`absolute inset-0 h-full w-full object-contain ${showSaveVid ? "opacity-100" : "pointer-events-none opacity-0"}`}>
              <source src={WEBM_SAVE} type="video/webm" />
              <source src={VIDEO_SAVE} type="video/mp4" />
            </video>
          </>
        )}
      </div>

      {/* footer — real interactive controls. GOAL/SAVED! headline is in the frozen video frame; we
          never duplicate it — for GOAL we add the (non-baked) coin reward, for SAVE just controls. */}
      <div className="relative z-20 w-full shrink-0 px-4 pt-2" style={{ paddingBottom: "calc(0.9rem + env(safe-area-inset-bottom))" }}>
        {isResult ? (
          <div className="mx-auto max-w-md rounded-2xl bg-black/55 p-4 text-center backdrop-blur" data-testid="ball-result">
            {goalOutcome ? (
              <div className="flex flex-col items-center gap-1">
                <span className="inline-flex items-center gap-1.5 rounded-full bg-[#14B8A6] px-4 py-1.5 text-lg font-extrabold text-white shadow" data-testid="ball-reward">+{result?.coins} <CoinIcon size={20} /></span>
                <p className="text-sm font-semibold text-white/90">ZRo Pe Coins added!</p>
              </div>
            ) : (
              <p className="font-display text-sm font-bold text-white/90" data-testid="ball-loss">No coins this time — give it another shot!</p>
            )}
            <p className="mt-1 text-xs text-white/70" data-testid="ball-remaining">{shotsLabel}</p>
            <div className="mt-3 flex gap-2">
              {remaining > 0 && (
                <button onClick={reset} data-testid="ball-playagain" className="flex flex-1 items-center justify-center gap-2 rounded-xl bg-gradient-to-br from-[#14B8A6] to-[#0D9488] px-4 py-3 text-sm font-bold text-white shadow transition active:scale-95"><RefreshCw size={16} /> Play again</button>
              )}
              <button onClick={close} data-testid="ball-back-games" className="flex flex-1 items-center justify-center gap-2 rounded-xl bg-white/90 px-4 py-3 text-sm font-bold text-[#0f172a] transition hover:bg-white active:scale-95"><Home size={16} /> Back to games</button>
            </div>
          </div>
        ) : (
          <>
            <button onClick={shoot} disabled={loading || played || playing} data-testid="ball-action"
              className="mx-auto flex w-full max-w-md items-center justify-center gap-2 rounded-2xl bg-gradient-to-br from-[#14B8A6] to-[#0D9488] px-6 py-4 text-center font-display text-lg font-extrabold uppercase tracking-wide text-white shadow-[0_8px_0_#0b6e64,0_12px_18px_rgba(0,0,0,.35)] transition active:translate-y-1 active:shadow-[0_4px_0_#0b6e64] disabled:opacity-60">
              {playing ? <><Loader2 size={18} className="animate-spin" /> Shooting…</> : played ? "No shots left today" : "Tap to play"}
            </button>
            <p className="mt-2 text-center text-xs text-white/80" data-testid="ball-remaining-top">{shotsLabel}</p>
          </>
        )}
      </div>
    </div>
  ), document.body);
}
